import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { getClient } from 'lane-shared/apollo';
import { ChipSelect, ChipStyle } from 'components/ads';

import { ServiceRequestStatusEnum } from 'domains/workOrder/serviceRequest/components/types/serviceRequestStatus';

import { Size } from 'components/ads/chipSelect/ChipSelect';
import Label from 'components/general/Label';

import styles from './StatusDropdown.scss';
import { updateServiceRequestStatusByIdMutation } from 'graphql-queries';
import { getServiceRequestStatusLabel } from 'domains-work-order-type-contracts';
import { safeConvertToUUID } from 'uuid-encoding';

const chipListColor: { [key: string]: ChipStyle } = {
  [ServiceRequestStatusEnum.Created]: ChipStyle.Green,
  [ServiceRequestStatusEnum.Accepted]: ChipStyle.Pink,
  [ServiceRequestStatusEnum.InProgress]: ChipStyle.Blue,
  [ServiceRequestStatusEnum.OnHold]: ChipStyle.Yellow,
  [ServiceRequestStatusEnum.Closed]: ChipStyle.Grey,
  [ServiceRequestStatusEnum.Declined]: ChipStyle.Grey,
  [ServiceRequestStatusEnum.Cancelled]: ChipStyle.Grey,
  [ServiceRequestStatusEnum.Complete]: ChipStyle.Grey,
};

type Props = {
  serviceRequestData: {
    serviceRequestId: string;
    status?: string | null;
    nextStatuses?: (string | null)[] | null;
  };
  isServiceRequestList?: boolean;
  refetch?: () => void;
  pillSize?: Size;
  disableEdit?: boolean;
  testId?: string;
};

const getChipType = (status?: string | null) =>
  (status && chipListColor[status]) || ChipStyle.Red;

export const ServiceRequestStatusDropdown = ({
  serviceRequestData,
  isServiceRequestList = false,
  refetch,
  pillSize,
  disableEdit = false,
  testId,
}: Props) => {
  const { t } = useTranslation();

  const [SRStatus, setSRStatus] = useState({
    label:
      getServiceRequestStatusLabel(serviceRequestData?.status ?? undefined) ??
      serviceRequestData?.status ??
      '',
    value: serviceRequestData?.status || '',
    type: getChipType(serviceRequestData?.status),
  });
  const [statusOptions, setStatusOptions] = useState([SRStatus]);

  const nextStatuses = serviceRequestData?.nextStatuses;

  useEffect(() => {
    const availableStatuses =
      nextStatuses
        ?.filter(singleStatus => singleStatus !== null)
        .map(singleStatus => {
          const label = getServiceRequestStatusLabel(singleStatus ?? undefined);

          return {
            label: label ?? singleStatus ?? '',
            value: singleStatus,
            type: getChipType(singleStatus),
          };
        })
        .filter(option => option.label !== '') || [];

    availableStatuses.push(SRStatus);
    setStatusOptions(availableStatuses);
  }, [SRStatus, nextStatuses]);

  useEffect(() => {
    if (serviceRequestData?.status) {
      const label = getServiceRequestStatusLabel(serviceRequestData?.status);

      setSRStatus({
        label: label ?? serviceRequestData?.status,
        value: serviceRequestData?.status,
        type: getChipType(serviceRequestData?.status),
      });
    }
  }, [serviceRequestData?.status]);

  const updateWorkOrderUCIStatus = async (value: string) => {
    if (value === ServiceRequestStatusEnum.Cancelled) {
      try {
        await window.Alert.confirm({
          title: t('web.admin.serviceRequest.cancel.modal.header'),
          message: t('web.admin.serviceRequest.cancel.modal.action.message'),
          cancelText: t(
            'web.admin.serviceRequest.cancel.modal.backbutton.text'
          ),
          confirmText: t('web.admin.serviceRequest.cancel.modal.button.text'),
        });
        // FIXME: Log error for datadog, missing stack trace
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (err) {
        // user "cancelled" their cancellatiion request
        return;
      }
    }

    try {
      await getClient().mutate({
        mutation: updateServiceRequestStatusByIdMutation,
        variables: {
          id: safeConvertToUUID(serviceRequestData?.serviceRequestId),
          status: value,
          skip: !serviceRequestData?.serviceRequestId,
        },
      });
      setSRStatus({
        label: getServiceRequestStatusLabel(value) ?? value,
        value,
        type: getChipType(serviceRequestData?.status),
      });

      if (refetch) {
        await refetch();
      }

      await window.Toast.show(
        t('web.admin.serviceRequest.status.update.success')
      );
    } catch (err) {
      await window.Alert.alert({
        title: t('web.admin.serviceRequest.status.update.failure'),
        error: err,
      });
    }
  };

  return (
    <div className={styles.chipSelectContainer}>
      {!isServiceRequestList && (
        <Label mb={1} mt={0}>
          {t('web.admin.serviceRequest.status')}
        </Label>
      )}
      <ChipSelect
        testId={
          testId ?? `statusSelect-${serviceRequestData?.serviceRequestId}`
        }
        anchorClassName={styles.anchor}
        value={SRStatus.value}
        list={statusOptions}
        size={pillSize}
        onChange={updateWorkOrderUCIStatus}
        isDisabled={disableEdit}
      />
    </div>
  );
};
